// src/components/barista/BaristaInterface.jsx
// Main barista interface component for managing orders - Tablet optimized

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Clock, Calendar, Users, ChefHat, CheckCircle, AlertCircle, Truck, MapPin, Coffee, Utensils, Package, Printer, RefreshCw, Filter, Search, X, Building2, ChevronDown, Home, Monitor } from 'lucide-react'
import BaristaService from '../../services/baristaService'
import BranchService from '../../services/branchService'
import OrderCard from './OrderCard'
import toast from 'react-hot-toast'
import { cn } from '../../utils/cn'
import { isManagerRole, isAdminRole } from '../../config/roles'

const BaristaInterface = ({ user, branchId, onLogout }) => {
  // State management
  const [orders, setOrders] = useState([]) // Currently filtered orders for display
  const [allOrders, setAllOrders] = useState([]) // All orders for counting
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('open') // 'open' or 'completed'
  const [filters, _setFilters] = useState({
    status: '',
    orderType: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  })
  const [currentTime, setCurrentTime] = useState(new Date())
  const [branches, setBranches] = useState([])
  const [showCloseShiftConfirm, setShowCloseShiftConfirm] = useState(false)
  const [selectedBranchId, setSelectedBranchId] = useState(branchId || '')
  const [showBranchSelector, setShowBranchSelector] = useState(false)
  const [branchesLoading, setBranchesLoading] = useState(false)
  const [completedOrderTimers, setCompletedOrderTimers] = useState(new Map()) // Track completion times

  // Navigation hook
  const navigate = useNavigate()

  // Check if user can select branches (admin or superadmin)
  const canSelectBranch = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'super_admin'

  // Check if user should see admin buttons (managers, admins, and super admins)
  const canAccessAdmin = isManagerRole(user?.role) || isAdminRole(user?.role)

  // Navigation handlers
  const handleAdminDashboardAccess = () => {
    console.log('🏠 Navigating to Admin Dashboard from Barista Interface')
    navigate('/admin/dashboard')
  }

  const handlePOSAccess = () => {
    console.log('🛒 Navigating to POS from Barista Interface')
    navigate('/pos')
  }

  // Memoized branch display name
  const selectedBranchDisplayName = useMemo(() => {
    if (!selectedBranchId) return 'All Branches'
    const selectedBranch = branches.find(b => b._id === selectedBranchId)
    return selectedBranch?.name || 'Selected Branch'
  }, [selectedBranchId, branches])

  // For non-admin users, fetch their specific branch details to get the name
  useEffect(() => {
    const fetchSpecificBranch = async () => {
      if (!canSelectBranch && selectedBranchId && branches.length === 0) {
        // First try to get branch info from user object
        if (user?.branch || user?.selectedBranch) {
          const branchFromUser = user.branch || user.selectedBranch
          if (typeof branchFromUser === 'object' && branchFromUser._id && branchFromUser.type !== 'warehouse') {
            setBranches([branchFromUser])
            return
          }
        }
        
        // If not available in user object, fetch from API
        try {
          const response = await BranchService.getBranchById(selectedBranchId)
          if (response.success && response.data) {
            const branchData = response.data.branch || response.data
            // Only set branch data if it's not a warehouse type
            if (branchData.type !== 'warehouse') {
              setBranches([branchData]) // Set just this one branch for display purposes
            }
          }
        } catch (error) {
          console.error('Error fetching specific branch:', error)
        }
      }
    }
    
    fetchSpecificBranch()
  }, [selectedBranchId, canSelectBranch, branches.length, user])

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000)
    return () => clearInterval(timer)
  }, [])

  // Update display every second to handle 20-second completion timer
  useEffect(() => {
    const timer = setInterval(() => {
      // Force a re-render to update the filtered orders
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [completedOrderTimers])

  // Fetch branches for admin/superadmin users
  const fetchBranches = useCallback(async () => {
    if (!canSelectBranch) return
    
    try {
      setBranchesLoading(true)
      const response = await BranchService.getAllBranches()
      
      if (response.success && response.data) {
        // Filter out warehouse-type branches for barista interface
        const filteredBranches = (response.data.branches || []).filter(branch => 
          branch.type !== 'warehouse'
        )
        setBranches(filteredBranches)
      }
    } catch (error) {
      console.error('Error fetching branches:', error)
      toast.error('Failed to load branches')
    } finally {
      setBranchesLoading(false)
    }
  }, [canSelectBranch])

  // Load branches on component mount
  useEffect(() => {
    if (canSelectBranch) {
      fetchBranches()
    }
  }, [canSelectBranch, fetchBranches])

  // Update selectedBranchId when branchId prop changes (for regular users)
  useEffect(() => {
    if (!canSelectBranch && branchId) {
      setSelectedBranchId(branchId)
    }
  }, [branchId, canSelectBranch])

  // Fetch orders - Moved before useEffect that references it
  const fetchOrders = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true)
      
      const params = {
        sortBy: 'createdAt',
        limit: 50,
        ...filters
      }
      
      // Branch filtering logic:
      // - For regular users: always use their assigned branchId
      // - For admin/superadmin: use selectedBranchId if set, otherwise show all branches
      let effectiveBranchId = null
      
      if (canSelectBranch) {
        // Admin/superadmin can select branches
        if (selectedBranchId) {
          effectiveBranchId = selectedBranchId
          console.log('🔍 Admin selected specific branch:', selectedBranchId)
        } else {
          console.log('🔍 Admin selected "All Branches" - no branch filter')
        }
      } else {
        // Regular users are restricted to their branch
        effectiveBranchId = branchId
        console.log('🔍 Regular user restricted to branch:', branchId)
      }
      
      console.log('🏢 Final branch filtering:', {
        canSelectBranch,
        selectedBranchId,
        branchId,
        effectiveBranchId,
        userRole: user?.role
      })
      
      if (effectiveBranchId) {
        params.branchId = effectiveBranchId
        console.log('✅ Adding branchId filter to API params:', effectiveBranchId)
      } else {
        console.log('⚠️ No branchId filter - API will return all branches')
      }
      
      // Always fetch ALL orders regardless of tab, then filter on frontend
      // This ensures we have complete data for accurate tab counts

      console.log('🔍 BaristaInterface: Fetching orders with final params:', params)

      const response = await BaristaService.getBaristaOrders(params)
      
      // console.log('🔍 Full API response:', response)
      
      let allOrders = []
      if (response.success && response.data && response.data.baristaOrders) {
        allOrders = response.data.baristaOrders
      } else if (response.baristaOrders) {
        allOrders = response.baristaOrders
      } else {
        allOrders = []
      }

      // Frontend filtering based on active tab (since backend doesn't support multiple statuses)
      let filteredOrders = allOrders
      if (activeTab === 'open') {
        // Show pending, preparing orders, AND completed orders within 20-second grace period
        const currentTime = Date.now()
        filteredOrders = allOrders.filter(order => {
          if (order.status === 'pending' || order.status === 'preparing') {
            return true
          }
          if (order.status === 'completed') {
            // Check if this order is still within its 20-second grace period
            const completionTime = completedOrderTimers.get(order._id)
            if (completionTime && (currentTime - completionTime) < 20000) {
              return true // Still within 20-second window
            }
          }
          return false
        })
        if (import.meta.env.DEV) {
          console.log('🔍 Open tab: Filtered orders:', filteredOrders.length, 'out of', allOrders.length)
        }
      } else if (activeTab === 'completed') {
        // Show completed orders (but not those in grace period on open tab)
        const currentTime = Date.now()
        filteredOrders = allOrders.filter(order => {
          if (order.status === 'completed') {
            // Only show in completed tab if NOT in grace period (or grace period expired)
            const completionTime = completedOrderTimers.get(order._id)
            if (!completionTime || (currentTime - completionTime) >= 20000) {
              return true
            }
          }
          return false
        })
        if (import.meta.env.DEV) {
          console.log('🔍 Completed tab: Filtered orders:', filteredOrders.length, 'out of', allOrders.length)
        }
      }

      // Debug: Status breakdown (can be removed in production)
      if (import.meta.env.DEV) {
        console.log('🔍 Status breakdown:', {
          total: allOrders.length,
          pending: allOrders.filter(o => o.status === 'pending').length,
          preparing: allOrders.filter(o => o.status === 'preparing').length,
          completed: allOrders.filter(o => o.status === 'completed').length
        })
      }

      // Store both all orders and filtered orders
      setAllOrders(allOrders)
      setOrders(filteredOrders)
    } catch (error) {
      console.error('❌ BaristaInterface: Error fetching barista orders:', error)
      
      if (showLoading) {
        toast.error('Failed to load orders. Please check your connection.')
      }
      setOrders([])
    } finally {
      if (showLoading) setLoading(false)
    }
  }, [branchId, filters, activeTab, canSelectBranch, selectedBranchId, user?.role, completedOrderTimers])

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading) {
        fetchOrders(false) // Silent refresh
      }
    }, 30000)
    
    return () => clearInterval(interval)
  }, [loading, fetchOrders])

  // Initial load
  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  // Close branch selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showBranchSelector && !event.target.closest('.branch-selector')) {
        setShowBranchSelector(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showBranchSelector])

  // Handle status change
  const handleStatusChange = async (orderId, newStatus) => {
    try {
      console.log(`🔄 Changing order ${orderId} status from current to:`, newStatus)
      
      await BaristaService.updateBaristaOrderStatus(orderId, newStatus)
      
      // Update local state immediately for better UX
      setOrders(prev => prev.map(order => 
        order._id === orderId 
          ? { ...order, status: newStatus }
          : order
      ))
      
      // Show appropriate success message
      let message = ''
      switch (newStatus) {
        case 'preparing':
          message = 'Order preparation started'
          break
        case 'completed':
          message = 'Order marked as completed'
          break
        default:
          message = `Order status updated to ${newStatus}`
      }
      toast.success(message)
      
      // If order is completed, start 20-second timer
      if (newStatus === 'completed') {
        const completionTime = Date.now()
        setCompletedOrderTimers(prev => new Map(prev.set(orderId, completionTime)))
        
        // Set timer to remove from open orders after 20 seconds
        setTimeout(() => {
          setCompletedOrderTimers(prev => {
            const newMap = new Map(prev)
            newMap.delete(orderId)
            return newMap
          })
          
          // Auto-refresh to ensure it moves to completed tab
          if (activeTab === 'open') {
            fetchOrders(false)
          }
        }, 20000) // 20 seconds
      }
      
      // Refresh to get updated data from server
      setTimeout(() => {
        fetchOrders(false)
      }, 500) // Small delay to ensure server has processed the change
      
    } catch (error) {
      console.error('❌ Error updating order status:', error)
      toast.error('Failed to update order status. Please try again.')
      
      // Refresh to ensure UI state matches server state
      fetchOrders(false)
    }
  }

  // Handle print (placeholder)
  const handlePrint = (order) => {
    console.log('Printing bill for order:', order.order.orderNumber)
    toast.success('Bill sent to printer')
  }

  // Handle close shift with logout
  const handleCloseShift = async () => {
    try {
      // Show confirmation with toast
      if (!showCloseShiftConfirm) {
        setShowCloseShiftConfirm(true)
        toast((t) => (
          <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-6 max-w-sm mx-auto">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <ChefHat className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Close Shift</h3>
                <p className="text-sm text-gray-500">End your work session</p>
              </div>
            </div>
            
            {/* Message */}
            <div className="mb-6">
              <p className="text-gray-700 text-sm leading-relaxed">
                Are you sure you want to close your shift and log out? This will end your current work session.
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  toast.dismiss(t.id)
                  setShowCloseShiftConfirm(false)
                  proceedWithCloseShift()
                }}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-lg font-semibold text-sm transition-colors duration-200 shadow-md hover:shadow-lg"
              >
                Yes, Close Shift
              </button>
              <button
                onClick={() => {
                  toast.dismiss(t.id)
                  setShowCloseShiftConfirm(false)
                }}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-3 rounded-lg font-semibold text-sm transition-colors duration-200 border border-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ), {
          duration: 15000,
          position: 'top-center',
          style: {
            background: 'transparent',
            boxShadow: 'none',
            padding: 0,
          }
        })
        return
      }
    } catch (error) {
      console.error('Error showing confirmation:', error)
      setShowCloseShiftConfirm(false)
    }
  }

  // Proceed with actual close shift logic
  const proceedWithCloseShift = async () => {
    try {
      toast.success('Shift closed successfully. Logging out...', {
        duration: 2000,
      })

      // Call logout function after a brief delay
      setTimeout(() => {
        if (onLogout) {
          onLogout()
        }
      }, 1000)
    } catch (error) {
      console.error('Error closing shift:', error)
      toast.error('Error closing shift, but logging out anyway')
      
      // Still logout even if there's an error
      setTimeout(() => {
        if (onLogout) {
          onLogout()
        }
      }, 1000)
    }
  }

  // Get order type icon
  const getOrderTypeIcon = (orderType) => {
    switch (orderType) {
      case 'delivery':
        return <Truck className="h-4 w-4 text-blue-600" />
      case 'take_away':
      case 'takeaway':
        return <Package className="h-4 w-4 text-orange-600" />
      case 'dine_in':
      default:
        return <Utensils className="h-4 w-4 text-green-600" />
    }
  }

  // Calculate counts for tab badges from all orders (not filtered ones)
  const now = Date.now()
  const openOrdersCount = allOrders.filter(order => {
    if (order.status === 'pending' || order.status === 'preparing') {
      return true
    }
    if (order.status === 'completed') {
      // Include completed orders still in grace period for open count
      const completionTime = completedOrderTimers.get(order._id)
      if (completionTime && (now - completionTime) < 20000) {
        return true
      }
    }
    return false
  }).length
  
  const completedOrdersCount = allOrders.filter(order => {
    if (order.status === 'completed') {
      // Only count as completed if NOT in grace period
      const completionTime = completedOrderTimers.get(order._id)
      if (!completionTime || (now - completionTime) >= 20000) {
        return true
      }
    }
    return false
  }).length
  
  // Calculate additional statistics from all orders
  const pendingOrders = allOrders.filter(o => o.status === 'pending').length
  const preparingOrders = allOrders.filter(o => o.status === 'preparing').length

  return (
    <div className="min-h-screen bg-orange-500 text-white">
      {/* Header */}
      <div className="bg-orange-600 border-b border-orange-400 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center p-2">
              <img
                src="/logo.png"
                alt="TEZLAA Logo"
                className="w-full h-full object-contain"
                onError={(e) => {
                  // Fallback to Coffee icon if logo fails to load
                  e.target.style.display = 'none'
                  e.target.nextSibling.style.display = 'flex'
                }}
              />
              <Coffee className="h-10 w-10 hidden" />
            </div>
            <div>
              <div className="flex items-center space-x-3 mb-1">
                <h1 className="text-xl font-bold">Barista Screen</h1>
                {selectedBranchDisplayName && selectedBranchDisplayName !== 'All Branches' && (
                  <div className="flex items-center space-x-1 bg-white/15 px-2 py-1 rounded-md">
                    <MapPin className="h-3 w-3" />
                    <span className="text-sm font-semibold">{selectedBranchDisplayName}</span>
                  </div>
                )}
                {selectedBranchDisplayName === 'All Branches' && canSelectBranch && (
                  <div className="flex items-center space-x-1 bg-white/15 px-2 py-1 rounded-md">
                    <Building2 className="h-3 w-3" />
                    <span className="text-sm font-semibold">All Branches</span>
                  </div>
                )}
              </div>
              <p className="text-orange-100 text-xs flex items-center space-x-3">
                <span className="flex items-center space-x-1">
                  <ChefHat className="h-3 w-3" />
                  <span>{user?.username}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Calendar className="h-3 w-3" />
                  <span>{currentTime.toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric'
                  })}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Clock className="h-3 w-3" />
                  <span>{currentTime.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                </span>
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Admin Dashboard and POS Buttons - Show for managers, admins, and super admins */}
            {canAccessAdmin && (
              <>
                <button
                  onClick={handleAdminDashboardAccess}
                  className="flex items-center space-x-2 bg-white/15 hover:bg-white/25 px-3 py-2 rounded-lg font-semibold transition-colors text-xs"
                  title="Admin Dashboard"
                >
                  <Home className="h-3 w-3" />
                  <span>Dashboard</span>
                </button>
                
                <button
                  onClick={handlePOSAccess}
                  className="flex items-center space-x-2 bg-white/15 hover:bg-white/25 px-3 py-2 rounded-lg font-semibold transition-colors text-xs"
                  title="POS System"
                >
                  <Monitor className="h-3 w-3" />
                  <span>POS</span>
                </button>
              </>
            )}

            {/* Branch Selector for Admin/Superadmin */}
            {canSelectBranch && (
              <div className="relative branch-selector">
                <button
                  onClick={() => setShowBranchSelector(!showBranchSelector)}
                  disabled={branchesLoading}
                  className="flex items-center space-x-2 bg-white/10 hover:bg-white/20 px-3 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50"
                  title="Select Branch"
                >
                  <Building2 className="h-3 w-3" />
                  <span className="text-xs">
                    {selectedBranchDisplayName}
                  </span>
                  <ChevronDown className={cn("h-3 w-3 transition-transform", showBranchSelector && "rotate-180")} />
                </button>

                {/* Branch Dropdown */}
                {showBranchSelector && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-60 overflow-y-auto">
                    {/* All Branches Option */}
                    <button
                      onClick={() => {
                        console.log('🏢 Selecting All Branches')
                        setSelectedBranchId('')
                        setShowBranchSelector(false)
                      }}
                      className={cn(
                        "w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100",
                        !selectedBranchId && "bg-orange-50 text-orange-600 font-semibold"
                      )}
                    >
                      <div className="flex items-center space-x-2">
                        <Building2 className="h-4 w-4" />
                        <span>All Branches</span>
                      </div>
                    </button>

                    {/* Individual Branches - Filter out warehouse types */}
                    {branches.filter(branch => branch.type !== 'warehouse').map(branch => (
                      <button
                        key={branch._id}
                        onClick={() => {
                          console.log(`🏢 Selecting branch: ${branch.name} (${branch._id})`)
                          setSelectedBranchId(branch._id)
                          setShowBranchSelector(false)
                        }}
                        className={cn(
                          "w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors",
                          selectedBranchId === branch._id && "bg-orange-50 text-orange-600 font-semibold"
                        )}
                      >
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4" />
                          <div>
                            <div className="font-medium text-gray-900">{branch.name}</div>
                            {branch.address && (
                              <div className="text-xs text-gray-500">{branch.address}</div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}

                    {branches.length === 0 && !branchesLoading && (
                      <div className="px-4 py-3 text-gray-500 text-sm">
                        No branches available
                      </div>
                    )}

                    {branchesLoading && (
                      <div className="px-4 py-3 text-gray-500 text-sm flex items-center space-x-2">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        <span>Loading branches...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Refresh Button */}
            <button
              onClick={() => fetchOrders()}
              disabled={loading}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh Orders"
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </button>

            {/* Close Shift Button */}
            <button 
              onClick={handleCloseShift}
              className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg font-semibold transition-colors text-xs"
            >
              Close Shift
            </button>
          </div>
        </div>

        {/* Tabs and Statistics Row */}
        <div className="flex items-center justify-between mt-4">
          {/* Tabs */}
          <div className="flex space-x-3">
            <button
              onClick={() => setActiveTab('open')}
              className={cn(
                "px-4 py-2 rounded-lg font-bold transition-colors text-xs flex items-center space-x-2",
                activeTab === 'open' 
                  ? "bg-white text-orange-600 shadow-md" 
                  : "bg-white/10 hover:bg-white/20"
              )}
            >
              <AlertCircle className="h-3 w-3" />
              <span>Open ({openOrdersCount})</span>
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={cn(
                "px-4 py-2 rounded-lg font-bold transition-colors text-xs flex items-center space-x-2",
                activeTab === 'completed' 
                  ? "bg-white text-orange-600 shadow-md" 
                  : "bg-white/10 hover:bg-white/20"
              )}
            >
              <CheckCircle className="h-3 w-3" />
              <span>Completed ({completedOrdersCount})</span>
            </button>
          </div>

          {/* Order Statistics - Compact horizontal layout */}
          {orders.length > 0 && (
            <div className="flex space-x-4">
              <div className="bg-white/10 rounded-lg px-3 py-2">
                <div className="text-white/70 text-xs font-semibold">PENDING</div>
                <div className="text-lg font-bold text-white">{pendingOrders}</div>
              </div>
              <div className="bg-white/10 rounded-lg px-3 py-2">
                <div className="text-white/70 text-xs font-semibold">PREPARING</div>
                <div className="text-lg font-bold text-white">{preparingOrders}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <RefreshCw className="h-10 w-10 animate-spin mx-auto mb-4" />
              <p className="text-base">Loading orders...</p>
            </div>
          </div>
        ) : orders.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Coffee className="h-16 w-16 mx-auto mb-6 opacity-50" />
              <h3 className="text-xl font-bold mb-2">No orders found</h3>
              <p className="text-orange-200 text-base">
                {activeTab === 'open' 
                  ? 'No pending orders at the moment' 
                  : 'No completed orders found'
                }
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
            {orders.map((order) => (
              <OrderCard 
                key={order._id} 
                order={order}
                onStatusChange={handleStatusChange}
                onPrint={handlePrint}
                getOrderTypeIcon={getOrderTypeIcon}
                completedOrderTimers={completedOrderTimers}
                activeTab={activeTab}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default BaristaInterface
